/**
 * 跨标签页消息总线。
 *
 * channel 的生命周期是页面，订阅的生命周期才是组件。反过来把 channel 交给组件持有，
 * 每次重挂都会留下一个开着的 orphan——那是本项目此前两条 channel 全部泄漏的根因。
 */
export interface ITabChannel<TMessage> {
  /** 投给本页订阅者，也投给其他标签页 */
  publishToAll: (message: TMessage) => void;
  /** 只投给其他标签页。本页对自己刚做的事已经知情，收到自己的公告只会互相干扰 */
  publishToOthers: (message: TMessage) => void;
  /** 只投给本页订阅者。本页自查出的状态不是新闻，不该惊动其他标签页 */
  publishToSelf: (message: TMessage) => void;
  /** 返回退订函数 */
  subscribe: (listener: (message: TMessage) => void) => () => void;
  /** 仅供测试隔离，业务代码禁止调用 */
  close: () => void;
}

/**
 * `parse` 是入站边界：`BroadcastChannel` 的 data 由其他标签页写入，形态对不上就丢弃。
 * 因此消息类型不能取 `undefined` 值，该值被用来表示「这条不认」。
 */
export function createTabChannel<TMessage>(
  name: string,
  parse: (data: unknown) => TMessage | undefined
): ITabChannel<TMessage> {
  const listeners = new Set<(message: TMessage) => void>();
  let channel: BroadcastChannel | undefined;

  const publishToSelf = (message: TMessage) => {
    // 遍历快照：订阅方可以在回调里退订，直接遍历 Set 的行为取决于遍历时机
    for (const listener of [...listeners]) listener(message);
  };

  const ensureChannel = () => {
    if (channel) return channel;
    const opened = new BroadcastChannel(name);
    opened.addEventListener('message', (event: MessageEvent<unknown>) => {
      const message = parse(event.data);
      if (message !== undefined) publishToSelf(message);
    });
    channel = opened;
    return opened;
  };

  return {
    publishToAll: (message) => {
      // BroadcastChannel 不投给发送它的那个 channel 对象，本页的分发必须显式做
      publishToSelf(message);
      ensureChannel().postMessage(message);
    },
    publishToOthers: (message) => {
      ensureChannel().postMessage(message);
    },
    publishToSelf,
    subscribe: (listener) => {
      ensureChannel();
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    close: () => {
      channel?.close();
      channel = undefined;
      listeners.clear();
    }
  };
}
